import { ChartDataLoaded } from '@g17eco/types/insight-custom-dashboard';
import { getChartOptions, chartStyleNames, toChartProps } from '@routes/summary/insights/utils/chartStyles';
import { ChartType, chartTooltipOptions } from '@routes/summary/insights/utils/constants';
import Chart from 'react-google-charts';
import { BaseChartComponentProps, BaseHistoricalUtr, ChartDataProps } from '../types';
import { getVAxisTitle, hasChartDataRows } from './multi-utrs-chart/utils';
import { useChartData } from './useChartData';

type Props<T extends BaseHistoricalUtr> = BaseChartComponentProps<T> & {
  isStacked?: boolean;
  getChartData: (props: ChartDataProps<T>) => Promise<ChartDataLoaded>;
  /** Explicit axis-title override; pass '' to render no title without the metric-unit fallback. */
  vAxisTitle?: string;
};

export const ColumnChart = <T extends BaseHistoricalUtr>(props: Props<T>) => {
  const { getChartData, isStacked, overrideOptions, calculation, utrsData, unitText, vAxisTitle: vAxisTitleOverride } = props;
  const data = useChartData(getChartData, props);

  if (!calculation || !data || !hasChartDataRows(data.chartData)) {
    return null;
  }

  const vAxisTitle = vAxisTitleOverride ?? getVAxisTitle({ unitText, calculation, utrsData });

  return (
    <div className='w-100 h-100' data-testid='column-chart'>
      <Chart
        chartType={ChartType.ColumnChart}
        data={data.chartData}
        width={'100%'}
        height={'100%'}
        options={{
          ...getChartOptions({
            chartType: ChartType.ColumnChart,
            vAxisTitle,
            options: isStacked ? chartStyleNames.chartStyleStacked : undefined,
            chartProps: toChartProps(props),
          }),
          tooltip: chartTooltipOptions,
          ...overrideOptions,
        }}
      />
    </div>
  );
};
